import React, { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { api, initPortal, setAccessToken, setRefreshToken } from '@repo/api-sdk';
import type {
  AuthContextType,
  AuthState,
  PortalType,
  VerifyOtpData,
  ResetPasswordData,
} from '../types/auth.types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
  portal: PortalType;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, portal }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    portal,
    isAuthenticated: false,
    isInitialLoading: true,
  });

  // Initialize the shared API portal on mount
  useEffect(() => {
    initPortal(portal);
  }, [portal]);

  // Listen for forced logout events dispatched by the API interceptor (e.g. expired token + no refresh)
  // Resets React state — ProtectedRoute automatically redirects to /login when isAuthenticated becomes false
  useEffect(() => {
    const handleForceLogout = () => {
      setState((prev) => ({ ...prev, user: null, isAuthenticated: false }));
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  const login = async (credentials: any) => {
    try {
      const response = await api.post(`/${portal}/auth/signin`, credentials);
      const { accessToken, refreshToken } = response.data;
      setAccessToken(accessToken);
      if (refreshToken) {
        setRefreshToken(refreshToken);
      }

      // Fetch complete user data from verify-token (signin only returns tokens)
      const verifyResponse = await api.post(`/${portal}/auth/verify-token`);
      const userData = verifyResponse.data;
      setState((prev) => ({ ...prev, user: userData, isAuthenticated: true }));
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post(`/${portal}/auth/logout`);
    } finally {
      setAccessToken(null);
      setRefreshToken(null);
      setState((prev) => ({ ...prev, user: null, isAuthenticated: false }));
    }
  };

  const forgotPassword = async (email: string) => {
    await api.post(`/${portal}/auth/forgot-password`, { email });
  };

  const verifyOtp = async (data: VerifyOtpData) => {
    await api.post(`/${portal}/auth/verify-otp`, data);
  };

  const resetPassword = async (data: ResetPasswordData) => {
    await api.post(`/${portal}/auth/reset-password`, data);
  };

  const refreshTokenFn = async (refreshToken: string): Promise<string> => {
    const response = await api.post(`/${portal}/auth/refresh-token`, { refreshToken });
    const newToken = response.data.accessToken;
    setAccessToken(newToken);
    return newToken;
  };

  const updateUser = (patch: Partial<any>) => {
    setState((prev) => ({
      ...prev,
      user: prev.user ? { ...prev.user, ...patch } : prev.user,
    }));
  };

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const accessToken = Cookies.get(`${portal}AccessToken`);

        if (accessToken) {
          const response = await api.post(`/${portal}/auth/verify-token`);
          const userData = response.data;
          setState((prev) => ({
            ...prev,
            isAuthenticated: true,
            user: userData,
          }));
        } else {
          setState((prev) => ({ ...prev, isAuthenticated: false }));
        }
      } catch (error: any) {
        const status = error?.response?.status;
        const isNetworkError = !error?.response; // no response = server unreachable

        if (isNetworkError) {
          // Backend is down — keep the user authenticated if they have a token.
          // The interceptor will handle forced logout when a 401/403 is confirmed later.
          const hasToken = !!Cookies.get(`${portal}AccessToken`);
          setState((prev) => ({ ...prev, isAuthenticated: hasToken }));
        } else if (status === 401 || status === 403) {
          // Token is genuinely invalid — force logout
          setState((prev) => ({ ...prev, isAuthenticated: false }));
        } else {
          // Other server errors (500, etc.) — don't log the user out
          setState((prev) => ({ ...prev, isAuthenticated: true }));
        }
      } finally {
        setState((prev) => ({ ...prev, isInitialLoading: false }));
      }
    };

    checkSession();
  }, [portal]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        forgotPassword,
        verifyOtp,
        resetPassword,
        refreshToken: refreshTokenFn,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
